<template>
  <div class="auth-login-google">
    <GoogleSignInButton class="auth-login-google__button" @success="handleSignInSuccess" @error="handleLoginError" />
    <ElementsRecaptcha ref="recaptchaRef" />
  </div>
</template>

<script lang="ts" setup>
import {CredentialResponse, decodeCredential} from 'vue3-google-signin';
import {PropType, Ref} from 'vue';

// EMIT Definitions
//----------------------------------------------------------------------------------------------------------------------
const emit = defineEmits<{
  (event: 'update:loading', payload: boolean): void;
  (event: 'update:response', payload: 200 | 201 | 403 | 406 | 407 | 500 | null): void;
}>();

// PROPS Definition
//----------------------------------------------------------------------------------------------------------------------
const props = defineProps({
  loading: {
    type: Boolean,
    default: false,
  },
  response: {
    type: [Number, null] as PropType<200 | 201 | 403 | 406 | 407 | 500 | null>,
    default: null,
  },
});

// DATA Definitions
//----------------------------------------------------------------------------------------------------------------------
const {$register, $login} = useNuxtApp();
const recaptchaRef: Ref<HTMLElement | null> = ref(null);

// handle success event
const handleSignInSuccess = async (response: CredentialResponse) => {
  emit('update:loading', true);
  const { credential } = response;
  if (!credential) {
    return;
  }
  const decodedCredential = decodeCredential(credential);
  if (!decodedCredential || !decodedCredential.email || !decodedCredential.given_name || !decodedCredential.family_name || !decodedCredential.id) {
    emit('update:response', 500);
  }
  // generate recaptcha token
  const token = await (recaptchaRef.value as any).validate();
  const registrationResponse = await $register({
    firstName: decodedCredential.given_name,
    lastName: decodedCredential.family_name,
    email: decodedCredential.email,
    avatarUrl: decodedCredential.picture || null,
    token,
    socialLoginData: {
      provider: 'google',
      token: decodedCredential.id,
    },
  });

  // Registration failed
  if (registrationResponse.code !== 201) {
    emit('update:response', registrationResponse.code);
  }

  // Registration successful and proceed to login
  if (registrationResponse.code === 201) {
    const loginResponse = await $login({
      email: decodedCredential.email,
      socialLoginData: {
        provider: 'google',
        token: decodedCredential.id,
      },
    });

    if (loginResponse.code !== 200) {
      emit('update:response', loginResponse.code);
    }
  }
};

// handle an error event
const handleLoginError = () => {
  emit('update:response', 500);
};
</script>

<style lang="scss">
.auth-login-google {
  &__button {
    &:first-child {
      > div {
        min-width: 200px;
        box-shadow: 0 0 4px 1px rgba(0, 0, 0, 0.1);
        transition: background 0.2s ease-in-out;

        &:hover {
          transition: background 0.2s ease-in-out;
        }
      }
    }
  }
}
</style>
